// Mirror the WebView's console (and uncaught errors) to the native app as
// `LOG` bridge messages, so the Expo app can surface or persist them — on-device
// WebView console output is otherwise invisible. Embed-only; the original
// console behavior is preserved (we tee, not replace).
const MAX = 12000  // raised for hydration diagnostics; the detailed mismatch + component trace is verbose

function serialize(args: any[]): string {
  const seen = new WeakSet()
  const out = args.map((a) => {
    if (a instanceof Error) return `${a.name}: ${a.message}${a.stack ? '\n' + a.stack : ''}`
    if (typeof a === 'string') return a
    try {
      return JSON.stringify(a, (_k, v) => {
        if (typeof v === 'object' && v !== null) { if (seen.has(v)) return '[Circular]'; seen.add(v) }
        if (typeof v === 'bigint') return v.toString()
        if (typeof v === 'function') return `[Function ${v.name || 'anonymous'}]`
        return v
      })
    } catch { return String(a) }
  }).join(' ')
  return out.length > MAX ? out.slice(0, MAX) + '… [truncated]' : out
}

export default defineNuxtPlugin({
  name: 'native-console',
  enforce: 'pre',   // before native-bootstrap, so exchange/bootstrap logs are mirrored too
  setup() {
    if (typeof window === 'undefined') return
    const bridge = useNativeBridge()
    if (!bridge.isNative.value) return

    const levels: Array<'log' | 'info' | 'warn' | 'error' | 'debug'> = ['log', 'info', 'warn', 'error', 'debug']
    for (const level of levels) {
      const original = (console as any)[level]?.bind(console)
      ;(console as any)[level] = (...args: any[]) => {
        try { bridge.post('LOG', { level, msg: serialize(args) }) } catch { /* never break logging */ }
        original?.(...args)
      }
    }

    // Uncaught errors + promise rejections → error-level LOG with location.
    window.addEventListener('error', (e) => {
      bridge.post('LOG', { level: 'error', msg: serialize([e.message, e.filename ? `(${e.filename}:${e.lineno}:${e.colno})` : '', e.error]) })
    })
    window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
      bridge.post('LOG', { level: 'error', msg: serialize(['unhandledrejection', (e as any).reason]) })
    })
  },
})
